import { useState } from "react";
import { Check, Search } from "lucide-react";
import { SlideOver } from "@/components/ui/slide-over";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { Empty } from "@/components/ui/notify/Empty";
import { Notify, toast } from "@/components/ui/notify";
import { useDebouncedValue } from "@/features/devices/manager/hooks/useDebouncedValue";
import { useOrgMachines } from "./hooks/useSandboxApi";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string | null;
  presentSourceIds: string[];
  onAdd: (m: {
    sourceMachineId: string;
    code: string;
    name: string;
    provenance?: "measured" | "assumed";
  }) => { ok: boolean; reason?: string };
}

// Seletor do acervo org-wide: busca, linha de origem, badge de histórico
// medido, pré-filtro por produto do cenário (evita 409). Bloqueia duplicidade de máquina presente.
export function AddMachineDialog({
  open,
  onOpenChange,
  productId,
  presentSourceIds,
  onAdd,
}: Props) {
  const [search, setSearch] = useState("");
  // Debounce evita uma request por tecla (o input em si permanece imediato).
  const debouncedSearch = useDebouncedValue(search, 300);
  const present = new Set(presentSourceIds);
  const orgMachinesQuery = useOrgMachines(
    { search: debouncedSearch || undefined, compatibleWithProductId: productId ?? undefined },
    open
  );
  const { data, isLoading, isError, error, refetch } = orgMachinesQuery;
  const items = data?.items ?? [];

  function handleAdd(m: (typeof items)[number]) {
    const r = onAdd({
      sourceMachineId: m.id,
      code: m.code,
      name: m.name,
      provenance: m.hasMeasuredHistory ? "measured" : "assumed",
    });
    if (!r.ok) toast.error("Não foi possível adicionar.", { description: r.reason });
    else toast.success(`${m.code} adicionada ao cenário.`);
  }

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title="Adicionar máquina do acervo">
      <div className="space-y-3 p-1">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código ou nome…"
            className="pl-8"
            aria-label="Buscar máquina"
          />
        </div>

        {isError ? (
          <Notify.QueryError error={error} onRetry={() => void refetch()} />
        ) : isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando acervo…</p>
        ) : items.length === 0 ? (
          <Empty
            title="Nenhuma máquina encontrada"
            description="Ajuste a busca ou o produto do cenário."
          />
        ) : (
          <ul className="space-y-1">
            {items.map((m) => {
              const already = present.has(m.id);
              return (
                <li
                  key={m.id}
                  className="flex items-center justify-between gap-2 rounded border px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{m.name}</span>
                      <span className="font-mono text-xs text-muted-foreground">{m.code}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{m.lineName}</span>
                      <Pill tone={m.hasMeasuredHistory ? "success" : "neutral"} withDot>
                        {m.hasMeasuredHistory ? "Histórico medido" : "Sem histórico"}
                      </Pill>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={already}
                    onClick={() => handleAdd(m)}
                  >
                    {already ? <Check className="h-4 w-4" /> : null}
                    {already ? "No cenário" : "Adicionar"}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </SlideOver>
  );
}
